import type { MenuItemProps } from "@mui/material";
import {
  Box,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";
import { ListHighlight } from "@ssf/components";
import { participantsFromTournamentsQuery } from "@ssf/memdb-components";
import { useQuery } from "@tanstack/react-query";
import { Searcher } from "fast-fuzzy";
import { Close, Magnify } from "mdi-material-ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { generatePath, useY2cTournaments } from "../lib/index.js";

const isMac = (
  (navigator as { userAgentData?: { platform?: string } }).userAgentData
    ?.platform ||
  navigator.platform ||
  "unknown"
)
  .toUpperCase()
  .includes("MAC");

export const useTeamSearch = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        setOpen((open) => !open);
      }
    };

    addEventListener("keydown", handleKeyDown);

    return () => removeEventListener("keydown", handleKeyDown);
  }, []);

  return { open, setOpen };
};

export const TeamSearchMenuItem = ({
  onClick,
  setOpen,
}: Pick<MenuItemProps, "onClick"> & ReturnType<typeof useTeamSearch>) => {
  const { t } = useTranslation("TeamSearch");

  return (
    <MenuItem
      onClick={(event) => {
        onClick?.(event);
        setOpen(true);
      }}
    >
      <ListItemText>{t("search")}</ListItemText>
      <Typography variant="body2" color="text.secondary">
        {isMac ? "⌘K" : "Ctrl + K"}
      </Typography>
    </MenuItem>
  );
};

export const TeamSearch = ({
  open,
  setOpen,
}: ReturnType<typeof useTeamSearch>) => {
  const { t } = useTranslation("TeamSearch");
  const navigate = useNavigate();
  const { tournaments } = useY2cTournaments();

  const { data: contenders, isLoading } = useQuery({
    ...participantsFromTournamentsQuery(tournaments.map(({ id }) => id)),
    enabled: open && Boolean(tournaments.length),
    select(tournaments) {
      return tournaments
        .flatMap((contenders) => [
          ...new Map(
            contenders.map((contender) => [contender.team.id, contender]),
          ).values(),
        ])
        .sort((a, b) => a.team.name.localeCompare(b.team.name));
    },
  });

  const [searchValue, setSearchValue] = useState("");

  const searcher = useMemo(
    () =>
      new Searcher(contenders || [], {
        keySelector: (contender) => contender.team.name,
      }),
    [contenders],
  );

  const filteredContenders = useMemo(
    () => (searchValue ? searcher.search(searchValue) : contenders),
    [searchValue, searcher, contenders],
  );

  useEffect(() => {
    if (!open) {
      setSearchValue("");
    }
  }, [open]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Unstable dependencies
  const handleSelect = useCallback(
    ({ option }: { option: NonNullable<typeof contenders>[0] }) => {
      navigate(
        generatePath("team", {
          tournamentId: option.tournamentId,
          teamId: option.team.id,
        }),
      );
      setOpen(false);
    },
    [navigate],
  );

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { alignSelf: "flex-start" } }}
    >
      <Box
        component="label"
        display="flex"
        alignItems="center"
        gap={1}
        px={2}
        py={1}
      >
        <Magnify />
        <InputBase
          autoFocus
          fullWidth
          placeholder={t("search")}
          onChange={({ currentTarget }) => setSearchValue(currentTarget.value)}
        />
        <IconButton onClick={() => setOpen(false)}>
          <Close />
        </IconButton>
      </Box>
      <Divider />
      {isLoading ? (
        <CircularProgress
          color="inherit"
          size="1.5rem"
          sx={{ mx: "auto", my: 2 }}
        />
      ) : filteredContenders?.length ? (
        <ListHighlight
          options={filteredContenders}
          disabled={!open}
          getKey={({ option }) => `${option.tournamentId}-${option.team.id}`}
          renderItem={({ option }) => (
            <ListItemButton
              component={Link}
              to={generatePath("team", {
                tournamentId: option.tournamentId,
                teamId: option.team.id,
              })}
              onClick={(event) => {
                if (!event.metaKey) {
                  event.preventDefault();
                  handleSelect({ option });
                }
              }}
            >
              <ListItemText>{option.team.name}</ListItemText>
            </ListItemButton>
          )}
          onSelect={handleSelect}
        />
      ) : (
        <List dense>
          <ListItem>{t("noResults")}</ListItem>
        </List>
      )}
    </Dialog>
  );
};
